'use client';

import {
    faExternalLinkAlt,
    faFilter,
    faMapMarkerAlt,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import clientsData from '@/textContent/logos/logosData.json';
import { motion } from 'framer-motion';
import { useState } from 'react';

const ClientsGrid = () => {
    const [selectedLocation, setSelectedLocation] = useState('All');
    const [selectedClient, setSelectedClient] = useState(null);
    const [imageErrors, setImageErrors] = useState({});

    // Get unique locations
    /* eslint-disable no-undef */
    const locations = [
        'All',
        ...new Set(
            clientsData.map((client) => client.location).filter(Boolean),
        ),
    ];
    /* eslint-enable no-undef */

    // Filter clients based on selected location
    const filteredClients =
        selectedLocation === 'All'
            ? clientsData
            : clientsData.filter(
                  (client) => client.location === selectedLocation,
              );

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            },
        },
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 50 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: 'easeOut',
            },
        },
    };

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.3,
            },
        },
        exit: {
            opacity: 0,
            scale: 0.8,
            transition: {
                duration: 0.2,
            },
        },
    };

    const handleImageError = (clientName) => {
        setImageErrors((prev) => ({ ...prev, [clientName]: true }));
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
            {/* Header Section */}
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-7xl mx-auto mb-8 sm:mb-12"
            >
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-center text-gray-900 mb-3 sm:mb-4 px-2">
                    Our Valued Clients
                </h1>
                <p className="text-center text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto px-4">
                    Trusted by leading organizations across multiple regions
                </p>
            </motion.div>

            {/* Filter Section */}
            <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="max-w-7xl mx-auto mb-6 sm:mb-8"
            >
                <div className="flex items-center justify-center gap-2 flex-wrap px-2">
                    <FontAwesomeIcon
                        icon={faFilter}
                        className="text-gray-500 text-sm sm:text-base"
                    />
                    <span className="text-gray-700 font-medium text-xs sm:text-sm md:text-base">
                        Filter by Location:
                    </span>
                    {locations.map((location) => (
                        <motion.button
                            key={location}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setSelectedLocation(location)}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                                selectedLocation === location
                                    ? 'bg-blue-600 text-white shadow-lg'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 shadow'
                            }`}
                        >
                            {location}
                        </motion.button>
                    ))}
                </div>
            </motion.div>

            {/* Clients Grid */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
            >
                {filteredClients.map((client, index) => (
                    <motion.div
                        key={index}
                        variants={cardVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer group"
                        onClick={() => setSelectedClient(client)}
                    >
                        {/* Image Container */}
                        <div className="relative h-40 sm:h-48 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
                            <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-6">
                                {!imageErrors[client.name] ? (
                                    <Image
                                        src={`/images/clients/${client.src}`}
                                        alt={client.name}
                                        width={200}
                                        height={150}
                                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform duration-300"
                                        onError={() =>
                                            handleImageError(client.name)
                                        }
                                    />
                                ) : (
                                    <div className="text-3xl sm:text-4xl font-bold text-gray-300">
                                        {client.name.charAt(0)}
                                    </div>
                                )}
                            </div>
                            {client.location && (
                                <div className="absolute top-2 sm:top-3 right-2 sm:right-3 bg-white bg-opacity-90 backdrop-blur-sm px-2 sm:px-3 py-1 rounded-full shadow-md">
                                    <FontAwesomeIcon
                                        icon={faMapMarkerAlt}
                                        className="text-red-500 mr-1 text-xs"
                                    />
                                    <span className="text-xs font-medium text-gray-700">
                                        {client.location}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Content */}
                        <div className="p-4 sm:p-5">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                {client.name}
                            </h3>
                            {client.clientDescription && (
                                <p className="text-xs sm:text-sm text-gray-600 line-clamp-3 mb-3">
                                    {client.clientDescription}
                                </p>
                            )}
                            {client.link && (
                                <Link
                                    rel="canonical"
                                    href={client.link}
                                    target="_blank"
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium group/link"
                                >
                                    Visit Website
                                    <FontAwesomeIcon
                                        icon={faExternalLinkAlt}
                                        className="ml-1.5 sm:ml-2 text-xs group-hover/link:translate-x-1 transition-transform"
                                    />
                                </Link>
                            )}
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            {/* No Results */}
            {filteredClients.length === 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-center py-12"
                >
                    <p className="text-gray-500 text-base sm:text-lg">
                        No clients found for this location.
                    </p>
                </motion.div>
            )}

            {/* Modal for Client Details */}
            <AnimatePresence>
                {selectedClient && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedClient(null)}
                    >
                        <motion.div
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="relative h-48 sm:h-64 bg-gradient-to-br from-blue-100 to-indigo-100">
                                <button
                                    onClick={() => setSelectedClient(null)}
                                    className="
    absolute 
    top-3 sm:top-4 
    right-3 sm:right-4 
    w-9 h-9 sm:w-10 sm:h-10
    bg-white 
    rounded-full 
    flex items-center justify-center
    shadow-lg 
    hover:bg-gray-100 
    transition-colors 
    z-10
  "
                                    aria-label="Close"
                                >
                                    <FontAwesomeIcon
                                        icon={faTimes}
                                        className="text-gray-700 text-sm sm:text-base"
                                    />
                                </button>

                                <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
                                    {!imageErrors[selectedClient.name] ? (
                                        <Image
                                            src={`/images/clients/${selectedClient.src}`}
                                            alt={selectedClient.name}
                                            width={400}
                                            height={300}
                                            className="max-w-full max-h-full object-contain"
                                            onError={() =>
                                                handleImageError(
                                                    selectedClient.name,
                                                )
                                            }
                                        />
                                    ) : (
                                        <div className="text-4xl sm:text-6xl font-bold text-gray-300">
                                            {selectedClient.name.charAt(0)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 sm:p-8">
                                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-0 mb-4">
                                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                                        {selectedClient.name}
                                    </h2>
                                    {selectedClient.location && (
                                        <div className="bg-blue-50 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                                            <FontAwesomeIcon
                                                icon={faMapMarkerAlt}
                                                className="text-red-500 mr-1.5 sm:mr-2 text-xs sm:text-sm"
                                            />
                                            <span className="text-xs sm:text-sm font-medium text-gray-700">
                                                {selectedClient.location}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {selectedClient.clientDescription && (
                                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-6">
                                        {selectedClient.clientDescription}
                                    </p>
                                )}

                                {selectedClient.link && (
                                    <Link
                                        rel="canonical"
                                        href={selectedClient.link}
                                        target="_blank"
                                        className="inline-flex items-center bg-blue-600 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
                                    >
                                        Visit Website
                                        <FontAwesomeIcon
                                            icon={faExternalLinkAlt}
                                            className="ml-2"
                                        />
                                    </Link>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ClientsGrid;
